/**
 * Scheduling & Gantt Components
 * 
 * NON-NEGOTIABLE RULES:
 * - Humans set dates, AI suggests only
 * - Materials create hard visual blocks  
 * - Dependencies are explicit
 * - No silent shifts - all changes highlighted with reason
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock,
  Truck,
  Wrench,
  ClipboardCheck,
  Timer,
  Flag,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  X,
  Edit2,
  Link2,
  Unlink,
  Package,
  RefreshCw,
  Maximize2,
  Minimize2,
  Info,
  Play,
  Pause,
  ListChecks
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GlassPanel from './GlassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Task type icons
const TASK_TYPE_ICONS = {
  'TRADE': Wrench,
  'DELIVERY': Truck,
  'COLLECTION': Package,
  'INSPECTION': ClipboardCheck,
  'BUFFER': Timer,
  'MILESTONE': Flag
};

// Status colors
const STATUS_COLORS = {
  'PLANNED': 'var(--accent-primary)',
  'BLOCKED': 'var(--status-danger)',
  'AT_RISK': 'var(--status-warning)',
  'IN_PROGRESS': 'var(--status-info)',
  'COMPLETED': 'var(--status-success)',
  'CANCELLED': 'var(--text-muted)'
};

// Task type colors (default)
const TYPE_COLORS = {
  'TRADE': '#6366f1',
  'DELIVERY': '#22c55e',
  'COLLECTION': '#3b82f6',
  'INSPECTION': '#f59e0b',
  'BUFFER': '#94a3b8',
  'MILESTONE': '#8b5cf6'
};

// ============ HELPERS ============

function parseDate(str) {
  if (!str) return null;
  return new Date(str);
}

function formatDate(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatDateFull(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function diffDays(d1, d2) {
  const t1 = d1.getTime();
  const t2 = d2.getTime();
  return Math.round((t2 - t1) / (1000 * 60 * 60 * 24));
}

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ============ TASK STATUS BADGE ============

export function TaskStatusBadge({ status }) {
  const statusLabels = {
    'PLANNED': 'Planned',
    'BLOCKED': 'Blocked',
    'AT_RISK': 'At Risk',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'CANCELLED': 'Cancelled'
  };

  return (
    <span style={{
      fontSize: '0.6875rem',
      padding: '2px 8px',
      borderRadius: '10px',
      fontWeight: '500',
      background: `${STATUS_COLORS[status]}20`,
      color: STATUS_COLORS[status],
      whiteSpace: 'nowrap'
    }}>
      {statusLabels[status] || status}
    </span>
  );
}

// ============ TASK TYPE BADGE ============

export function TaskTypeBadge({ type }) {
  const Icon = TASK_TYPE_ICONS[type] || Clock;
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.6875rem',
      padding: '2px 6px',
      borderRadius: 'var(--radius-sm)',
      background: `${TYPE_COLORS[type]}15`,
      color: TYPE_COLORS[type]
    }}>
      <Icon size={10} />
      {type}
    </span>
  );
}

// ============ GANTT CHART ============

export function GanttChart({ jobId, orgId, onTaskClick }) {
  const { getAuthHeaders } = useAuth();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('WEEK'); // WEEK or DAY
  const [viewStart, setViewStart] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [hoveredTask, setHoveredTask] = useState(null);
  const containerRef = useRef(null);

  const fetchTimeline = useCallback(async () => {
    if (!jobId || !orgId) return;
    
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/scheduling/job/${jobId}/timeline?org_id=${orgId}`,
        { headers: getAuthHeaders() }
      );
      
      if (!res.ok) throw new Error('Failed to fetch timeline');
      const data = await res.json();
      setTimeline(data);
      
      // Initialize view start if not set
      if (!viewStart) {
        const start = parseDate(data.timeline_start);
        if (start) {
          // Start from beginning of week
          const day = start.getDay();
          const diff = start.getDate() - day + (day === 0 ? -6 : 1);
          setViewStart(new Date(start.setDate(diff)));
        }
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, orgId, getAuthHeaders, viewStart]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Generate date columns
  const dateColumns = useMemo(() => {
    if (!viewStart) return [];
    
    const cols = [];
    const numDays = viewMode === 'WEEK' ? 28 : 14; // 4 weeks or 2 weeks
    
    for (let i = 0; i < numDays; i++) {
      const date = addDays(viewStart, i);
      cols.push({
        date,
        dayName: date.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-GB', { month: 'short' }),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.toDateString() === new Date().toDateString(),
        weekNum: getWeekNumber(date)
      });
    }
    
    return cols;
  }, [viewStart, viewMode]);

  // Calculate task position
  const getTaskPosition = useCallback((task) => {
    if (!viewStart || !dateColumns.length) return null;
    
    const start = parseDate(task.start);
    const end = parseDate(task.end);
    if (!start || !end) return null;
    
    const firstDate = viewStart;
    const lastDate = dateColumns[dateColumns.length - 1].date;
    
    // Check if task is visible in current view
    if (end < firstDate || start > lastDate) return null;
    
    const startOffset = Math.max(0, diffDays(firstDate, start));
    const endOffset = Math.min(dateColumns.length - 1, diffDays(firstDate, end));
    
    const cellWidth = viewMode === 'WEEK' ? 40 : 60;
    
    return {
      left: startOffset * cellWidth,
      width: (endOffset - startOffset + 1) * cellWidth - 4,
      isPartial: start < firstDate || end > lastDate
    };
  }, [viewStart, dateColumns, viewMode]);

  const navigateTimeline = (direction) => {
    if (!viewStart) return;
    const days = viewMode === 'WEEK' ? 7 : 7;
    setViewStart(addDays(viewStart, direction * days));
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setViewStart(new Date(today.setDate(diff)));
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    if (onTaskClick) onTaskClick(task);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xl)' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-sm)', 
        color: 'var(--status-danger)',
        padding: 'var(--space-lg)' 
      }}>
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  const cellWidth = viewMode === 'WEEK' ? 40 : 60;

  return (
    <div data-testid="gantt-chart">
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-md)',
        gap: 'var(--space-md)',
        flexWrap: 'wrap'
      }}>
        {/* Left: Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <button 
            onClick={() => navigateTimeline(-1)}
            className="btn btn-secondary"
            style={{ padding: '6px 10px' }}
          >
            <ChevronLeft size={16} />
          </button>
          <button 
            onClick={goToToday}
            className="btn btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.75rem' }}
          >
            Today
          </button>
          <button 
            onClick={() => navigateTimeline(1)}
            className="btn btn-secondary"
            style={{ padding: '6px 10px' }}
          >
            <ChevronRight size={16} />
          </button>
          
          <span style={{ marginLeft: 'var(--space-md)', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            {viewStart && `${formatDate(viewStart)} - ${formatDate(addDays(viewStart, dateColumns.length - 1))}`}
          </span>
        </div>

        {/* Right: View Mode & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <div style={{
            display: 'flex',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-md)',
            padding: '2px'
          }}>
            <button
              onClick={() => setViewMode('WEEK')}
              style={{
                padding: '4px 12px',
                fontSize: '0.75rem',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: viewMode === 'WEEK' ? 'var(--accent-primary)' : 'transparent',
                color: viewMode === 'WEEK' ? 'white' : 'var(--text-secondary)'
              }}
              data-testid="view-mode-week"
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('DAY')}
              style={{
                padding: '4px 12px',
                fontSize: '0.75rem',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: viewMode === 'DAY' ? 'var(--accent-primary)' : 'transparent',
                color: viewMode === 'DAY' ? 'white' : 'var(--text-secondary)'
              }}
              data-testid="view-mode-day"
            >
              Day
            </button>
          </div>
          
          <button 
            onClick={fetchTimeline}
            className="btn btn-secondary"
            style={{ padding: '6px 10px' }}
          >
            <RefreshCw size={14} />
          </button>
          
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
            data-testid="add-task-btn"
          >
            <Plus size={14} />
            Add Task
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-lg)',
        marginBottom: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-md)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <ListChecks size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {timeline?.total_tasks || 0} tasks
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <CheckCircle size={14} style={{ color: 'var(--status-success)' }} />
          <span style={{ fontSize: '0.875rem', color: 'var(--status-success)' }}>
            {timeline?.completed_tasks || 0} complete
          </span>
        </div>
        {timeline?.blocked_tasks > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <AlertTriangle size={14} style={{ color: 'var(--status-danger)' }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--status-danger)' }}>
              {timeline.blocked_tasks} blocked
            </span>
          </div>
        )}
        {timeline?.at_risk_tasks > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <AlertCircle size={14} style={{ color: 'var(--status-warning)' }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--status-warning)' }}>
              {timeline.at_risk_tasks} at risk
            </span>
          </div>
        )}
      </div>

      {/* AI Suggestions */}
      {timeline?.ai_suggestions?.length > 0 && (
        <div style={{
          marginBottom: 'var(--space-lg)',
          padding: 'var(--space-md)',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(99, 102, 241, 0.2)'
        }}>
          <div style={{ 
            fontSize: '0.75rem', 
            color: 'var(--accent-primary)', 
            marginBottom: 'var(--space-xs)',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-xs)'
          }}>
            <Info size={12} />
            AI Suggestions (NON-BINDING)
          </div>
          {timeline.ai_suggestions.map((s, i) => (
            <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {s}
            </div>
          ))}
        </div>
      )}

      {/* Gantt Container */}
      <div 
        ref={containerRef}
        style={{ 
          overflowX: 'auto',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--glass-border)' }}>
          {/* Task name column */}
          <div style={{ 
            width: '200px', 
            minWidth: '200px',
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--glass-bg)',
            borderRight: '1px solid var(--glass-border)',
            fontWeight: '600',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            textTransform: 'uppercase'
          }}>
            Task
          </div>
          
          {/* Date columns */}
          <div style={{ display: 'flex' }}>
            {dateColumns.map((col, i) => (
              <div
                key={i}
                style={{
                  width: `${cellWidth}px`,
                  minWidth: `${cellWidth}px`,
                  textAlign: 'center',
                  padding: '4px 2px',
                  borderRight: '1px solid var(--glass-border)',
                  background: col.isToday ? 'rgba(99, 102, 241, 0.1)' : col.isWeekend ? 'var(--glass-bg)' : 'transparent',
                  fontSize: '0.6875rem'
                }}
              >
                <div style={{ color: col.isToday ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                  {col.dayName}
                </div>
                <div style={{ 
                  fontWeight: col.isToday ? '700' : '500',
                  color: col.isToday ? 'var(--accent-primary)' : 'var(--text-primary)'
                }}>
                  {col.dayNum}
                </div>
                {col.dayNum === 1 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.625rem' }}>
                    {col.month}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Task Rows */}
        {timeline?.tasks?.length > 0 ? (
          timeline.tasks.map((task, rowIndex) => {
            const pos = getTaskPosition(task);
            const Icon = TASK_TYPE_ICONS[task.task_type] || Clock;
            const isHovered = hoveredTask === task.id;
            
            return (
              <div 
                key={task.id}
                style={{ 
                  display: 'flex', 
                  borderBottom: '1px solid var(--glass-border)',
                  minHeight: '44px'
                }}
                data-testid={`gantt-row-${task.id}`}
              >
                {/* Task name */}
                <div 
                  style={{ 
                    width: '200px', 
                    minWidth: '200px',
                    padding: 'var(--space-sm) var(--space-md)',
                    borderRight: '1px solid var(--glass-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    cursor: 'pointer',
                    background: isHovered ? 'var(--glass-bg)' : 'transparent'
                  }}
                  onClick={() => handleTaskClick(task)}
                  onMouseEnter={() => setHoveredTask(task.id)}
                  onMouseLeave={() => setHoveredTask(null)}
                >
                  <Icon size={14} style={{ color: TYPE_COLORS[task.task_type], flexShrink: 0 }} />
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {task.title}
                    </div>
                    {task.assigned_trade && (
                      <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                        {task.assigned_trade}
                      </div>
                    )}
                  </div>
                  {task.is_blocked && (
                    <AlertTriangle size={12} style={{ color: 'var(--status-danger)', flexShrink: 0 }} />
                  )}
                  {task.is_at_risk && !task.is_blocked && (
                    <AlertCircle size={12} style={{ color: 'var(--status-warning)', flexShrink: 0 }} />
                  )}
                </div>
                
                {/* Timeline cells */}
                <div style={{ 
                  position: 'relative', 
                  display: 'flex', 
                  flex: 1 
                }}>
                  {/* Background cells */}
                  {dateColumns.map((col, i) => (
                    <div
                      key={i}
                      style={{
                        width: `${cellWidth}px`,
                        minWidth: `${cellWidth}px`,
                        borderRight: '1px solid var(--glass-border)',
                        background: col.isToday ? 'rgba(99, 102, 241, 0.05)' : col.isWeekend ? 'rgba(0,0,0,0.02)' : 'transparent'
                      }}
                    />
                  ))}
                  
                  {/* Task bar */}
                  {pos && (
                    <div
                      style={{
                        position: 'absolute',
                        left: pos.left + 2,
                        top: '8px',
                        width: pos.width,
                        height: '28px',
                        background: task.is_blocked 
                          ? 'var(--status-danger)' 
                          : task.is_at_risk 
                            ? 'var(--status-warning)'
                            : task.status === 'COMPLETED'
                              ? 'var(--status-success)'
                              : task.color || TYPE_COLORS[task.task_type],
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px',
                        fontSize: '0.6875rem',
                        color: 'white',
                        fontWeight: '500',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                        transition: 'box-shadow 0.2s'
                      }}
                      onClick={() => handleTaskClick(task)}
                      onMouseEnter={() => setHoveredTask(task.id)}
                      onMouseLeave={() => setHoveredTask(null)}
                      title={`${task.title}\n${task.start} → ${task.end}${task.blocking_details ? '\n' + task.blocking_details : ''}`}
                    >
                      {pos.width > 60 && task.title}
                      {task.dependencies?.length > 0 && (
                        <Link2 size={10} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ 
            padding: 'var(--space-xl)', 
            textAlign: 'center',
            color: 'var(--text-muted)'
          }}>
            <Calendar size={32} style={{ marginBottom: 'var(--space-sm)', opacity: 0.5 }} />
            <div>No tasks scheduled yet</div>
            <div style={{ fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>
              Click &quot;Add Task&quot; to start planning
            </div>
          </div>
        )}

        {/* PO Milestones */}
        {timeline?.delivery_milestones?.length > 0 && (
          <>
            <div style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--glass-bg)',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              fontWeight: '600',
              textTransform: 'uppercase',
              borderTop: '2px solid var(--glass-border)'
            }}>
              Material Deliveries
            </div>
            {timeline.delivery_milestones.map((milestone) => {
              const milestoneDate = parseDate(milestone.expected_date);
              if (!milestoneDate || !viewStart) return null;
              
              const dayOffset = diffDays(viewStart, milestoneDate);
              if (dayOffset < 0 || dayOffset >= dateColumns.length) return null;
              
              return (
                <div 
                  key={milestone.po_id}
                  style={{ display: 'flex', borderBottom: '1px solid var(--glass-border)', minHeight: '36px' }}
                >
                  <div style={{ 
                    width: '200px', 
                    minWidth: '200px',
                    padding: 'var(--space-sm) var(--space-md)',
                    borderRight: '1px solid var(--glass-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    fontSize: '0.8125rem'
                  }}>
                    <Truck size={14} style={{ color: 'var(--status-success)' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {milestone.po_number}
                    </span>
                    {milestone.is_blocking && (
                      <span style={{
                        fontSize: '0.625rem',
                        padding: '1px 4px',
                        background: 'var(--status-danger)',
                        color: 'white',
                        borderRadius: '4px'
                      }}>
                        BLOCKING
                      </span>
                    )}
                  </div>
                  <div style={{ position: 'relative', display: 'flex', flex: 1 }}>
                    {dateColumns.map((col, i) => (
                      <div
                        key={i}
                        style={{
                          width: `${cellWidth}px`,
                          minWidth: `${cellWidth}px`,
                          borderRight: '1px solid var(--glass-border)',
                          background: col.isToday ? 'rgba(99, 102, 241, 0.05)' : col.isWeekend ? 'rgba(0,0,0,0.02)' : 'transparent'
                        }}
                      />
                    ))}
                    {/* Milestone marker */}
                    <div
                      style={{
                        position: 'absolute',
                        left: dayOffset * cellWidth + cellWidth / 2 - 8,
                        top: '6px',
                        width: '16px',
                        height: '16px',
                        background: milestone.is_blocking ? 'var(--status-danger)' : 'var(--status-success)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={`${milestone.supplier} - ${milestone.expected_date}`}
                    >
                      <Truck size={8} style={{ color: 'white' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          jobId={jobId}
          orgId={orgId}
          onClose={() => setSelectedTask(null)}
          onUpdate={fetchTimeline}
        />
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        jobId={jobId}
        orgId={orgId}
        onSuccess={() => {
          setShowAddModal(false);
          fetchTimeline();
        }}
        existingTasks={timeline?.tasks || []}
        poMilestones={timeline?.delivery_milestones || []}
      />
    </div>
  );
}

// ============ ADD TASK MODAL ============

export function AddTaskModal({ isOpen, onClose, jobId, orgId, onSuccess, existingTasks, poMilestones }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'TRADE',
    planned_start: '',
    planned_end: '',
    assigned_trade: '',
    trade_contact: '',
    depends_on_task: '',
    depends_on_po: ''
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: 'TRADE',
      planned_start: '',
      planned_end: '',
      assigned_trade: '',
      trade_contact: '',
      depends_on_task: '',
      depends_on_po: ''
    });
    setError(null);
  };

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dependencies = [];
      if (formData.depends_on_task) {
        dependencies.push({
          dependency_type: 'TASK',
          depends_on_task_id: formData.depends_on_task
        });
      }
      if (formData.depends_on_po) {
        dependencies.push({
          dependency_type: 'MATERIAL',
          depends_on_po_id: formData.depends_on_po
        });
      }

      const res = await fetch(
        `${API_URL}/api/scheduling/job/${jobId}/tasks?org_id=${orgId}`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: formData.title,
            description: formData.description || null,
            task_type: formData.task_type,
            planned_start: formData.planned_start,
            planned_end: formData.planned_end,
            assigned_trade: formData.assigned_trade || null,
            trade_contact: formData.trade_contact || null,
            dependencies
          })
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create task');
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-xl)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        style={{
          background: 'var(--card-bg)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid var(--glass-border)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', margin: 0, fontSize: '1.125rem' }}>
            <Calendar size={20} />
            Add Task
          </h2>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            data-testid="close-add-task-modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-lg)' }}>
            {error && (
              <div style={{
                padding: 'var(--space-md)',
                background: 'var(--status-danger-bg)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-lg)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
                <span style={{ color: 'var(--status-danger)' }}>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Task Title *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., First Fix Plumbing"
                required
                data-testid="task-title-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  className="form-input"
                  value={formData.task_type}
                  onChange={e => setFormData(prev => ({ ...prev, task_type: e.target.value }))}
                  data-testid="task-type-select"
                >
                  <option value="TRADE">Trade Work</option>
                  <option value="DELIVERY">Delivery</option>
                  <option value="COLLECTION">Collection</option>
                  <option value="INSPECTION">Inspection</option>
                  <option value="BUFFER">Buffer/Wait</option>
                  <option value="MILESTONE">Milestone</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Assigned Trade</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.assigned_trade}
                  onChange={e => setFormData(prev => ({ ...prev, assigned_trade: e.target.value }))}
                  placeholder="e.g., Plumber"
                  data-testid="task-trade-input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.planned_start}
                  onChange={e => setFormData(prev => ({ ...prev, planned_start: e.target.value }))}
                  required
                  data-testid="task-start-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">End Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.planned_end}
                  onChange={e => setFormData(prev => ({ ...prev, planned_end: e.target.value }))}
                  required
                  data-testid="task-end-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
                rows={2}
                data-testid="task-description-input"
              />
            </div>

            {/* Dependencies */}
            <div style={{ 
              marginTop: 'var(--space-md)', 
              padding: 'var(--space-md)',
              background: 'var(--glass-bg)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                marginBottom: 'var(--space-sm)',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Dependencies (Optional)
              </div>

              {existingTasks?.length > 0 && (
                <div className="form-group" style={{ marginBottom: 'var(--space-sm)' }}>
                  <label className="form-label">Depends on Task</label>
                  <select
                    className="form-input"
                    value={formData.depends_on_task}
                    onChange={e => setFormData(prev => ({ ...prev, depends_on_task: e.target.value }))}
                    data-testid="task-depends-task-select"
                  >
                    <option value="">None</option>
                    {existingTasks.map(t => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {poMilestones?.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Depends on Material Delivery</label>
                  <select
                    className="form-input"
                    value={formData.depends_on_po}
                    onChange={e => setFormData(prev => ({ ...prev, depends_on_po: e.target.value }))}
                    data-testid="task-depends-po-select"
                  >
                    <option value="">None</option>
                    {poMilestones.map(m => (
                      <option key={m.po_id} value={m.po_id}>
                        {m.po_number} - {m.supplier} ({m.expected_date})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div style={{ 
            padding: 'var(--space-lg)', 
            borderTop: '1px solid var(--glass-border)',
            display: 'flex',
            gap: 'var(--space-md)',
            justifyContent: 'flex-end'
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              data-testid="submit-add-task-btn"
            >
              {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={16} />}
              Add Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ TASK DETAIL MODAL ============

export function TaskDetailModal({ task, jobId, orgId, onClose, onUpdate }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const Icon = TASK_TYPE_ICONS[task.task_type] || Clock;

  const handleStatusUpdate = async () => {
    if (!newStatus || !changeReason) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/scheduling/tasks/${task.id}/status?org_id=${orgId}`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: newStatus,
            change_reason: changeReason
          })
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update status');
      }

      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-xl)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        style={{
          background: 'var(--card-bg)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '450px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid var(--glass-border)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--space-sm)', 
              margin: 0, 
              fontSize: '1.125rem' 
            }}>
              <Icon size={20} style={{ color: TYPE_COLORS[task.task_type] }} />
              {task.title}
            </h2>
            <div style={{ 
              display: 'flex', 
              gap: 'var(--space-sm)', 
              marginTop: 'var(--space-sm)' 
            }}>
              <TaskTypeBadge type={task.task_type} />
              <TaskStatusBadge status={task.status} />
            </div>
          </div>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-lg)' }}>
          {/* Blocking Alert */}
          {task.is_blocked && (
            <div style={{
              padding: 'var(--space-md)',
              background: 'var(--status-danger-bg)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-lg)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-sm)',
                color: 'var(--status-danger)',
                fontWeight: '600',
                marginBottom: 'var(--space-xs)'
              }}>
                <AlertTriangle size={16} />
                BLOCKED
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--status-danger)' }}>
                {task.blocking_details || task.blocking_reason}
              </div>
            </div>
          )}

          {/* At Risk Alert */}
          {task.is_at_risk && !task.is_blocked && (
            <div style={{
              padding: 'var(--space-md)',
              background: 'rgba(249, 115, 22, 0.1)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-lg)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-sm)',
                color: 'var(--status-warning)',
                fontWeight: '600',
                marginBottom: 'var(--space-xs)'
              }}>
                <AlertCircle size={16} />
                AT RISK
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--status-warning)' }}>
                {task.risk_reason}
              </div>
            </div>
          )}

          {error && (
            <div style={{
              padding: 'var(--space-md)',
              background: 'var(--status-danger-bg)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
              <span style={{ color: 'var(--status-danger)' }}>{error}</span>
            </div>
          )}

          {/* Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                Start Date
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {task.start}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                End Date
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {task.end}
              </div>
            </div>
            {task.assigned_trade && (
              <div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  Assigned Trade
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  {task.assigned_trade}
                </div>
              </div>
            )}
            <div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                Duration
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {task.duration_days} day{task.duration_days !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Dependencies */}
          {task.dependencies?.length > 0 && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <div style={{ 
                fontSize: '0.6875rem', 
                color: 'var(--text-muted)', 
                marginBottom: 'var(--space-sm)',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Dependencies
              </div>
              {task.dependencies.map((depId, i) => (
                <div 
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    padding: 'var(--space-xs) var(--space-sm)',
                    background: 'var(--glass-bg)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: '4px'
                  }}
                >
                  <Link2 size={12} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    Task: {depId}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Status Update */}
          {!showStatusUpdate ? (
            <button
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: 'var(--space-lg)' }}
              onClick={() => setShowStatusUpdate(true)}
            >
              <Edit2 size={14} />
              Update Status
            </button>
          ) : (
            <div style={{ 
              marginTop: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'var(--glass-bg)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div className="form-group">
                <label className="form-label">New Status</label>
                <select
                  className="form-input"
                  value={newStatus}
                  onChange={e => setNewStatus(e.target.value)}
                >
                  <option value="">Select status...</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reason for Change *</label>
                <input
                  type="text"
                  className="form-input"
                  value={changeReason}
                  onChange={e => setChangeReason(e.target.value)}
                  placeholder="e.g., Work started on site"
                />
                <div style={{ 
                  fontSize: '0.6875rem', 
                  color: 'var(--text-muted)', 
                  marginTop: '4px' 
                }}>
                  Required: All changes must have a reason
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStatusUpdate(false);
                    setNewStatus('');
                    setChangeReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                  disabled={loading || !newStatus || !changeReason}
                >
                  {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                  Update
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ AT-RISK JOBS WIDGET (for Command Centre) ============

export function AtRiskJobsWidget({ orgId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;
      
      try {
        const res = await fetch(
          `${API_URL}/api/scheduling/at-risk-jobs?org_id=${orgId}`,
          { headers: getAuthHeaders() }
        );
        
        if (res.ok) {
          setData(await res.json());
        }
      } catch (err) {
        console.error('Error fetching at-risk jobs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, getAuthHeaders]);

  if (loading) {
    return (
      <GlassPanel title="Schedule Risks" titleIcon={AlertTriangle}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-lg)' }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      </GlassPanel>
    );
  }

  if (!data || data.total_at_risk === 0) {
    return (
      <GlassPanel title="Schedule Risks" titleIcon={AlertTriangle}>
        <div style={{ 
          textAlign: 'center', 
          padding: 'var(--space-lg)',
          color: 'var(--status-success)'
        }}>
          <CheckCircle size={24} style={{ marginBottom: 'var(--space-sm)' }} />
          <div>All jobs on track</div>
        </div>
      </GlassPanel>
    );
  }

  const riskColors = {
    'CRITICAL': 'var(--status-danger)',
    'HIGH': '#f97316',
    'MEDIUM': 'var(--status-warning)',
    'LOW': 'var(--text-muted)'
  };

  return (
    <GlassPanel title="Schedule Risks" titleIcon={AlertTriangle}>
      {/* Summary */}
      <div style={{ 
        display: 'flex', 
        gap: 'var(--space-md)', 
        marginBottom: 'var(--space-lg)',
        flexWrap: 'wrap'
      }}>
        {data.critical > 0 && (
          <span style={{ 
            padding: '2px 8px', 
            background: `${riskColors.CRITICAL}20`,
            color: riskColors.CRITICAL,
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {data.critical} Critical
          </span>
        )}
        {data.high > 0 && (
          <span style={{ 
            padding: '2px 8px', 
            background: `${riskColors.HIGH}20`,
            color: riskColors.HIGH,
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {data.high} High
          </span>
        )}
        {data.medium > 0 && (
          <span style={{ 
            padding: '2px 8px', 
            background: `${riskColors.MEDIUM}20`,
            color: riskColors.MEDIUM,
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {data.medium} Medium
          </span>
        )}
      </div>

      {/* Jobs List */}
      {data.jobs.slice(0, 5).map(job => (
        <div 
          key={job.job_id}
          style={{
            padding: 'var(--space-sm)',
            borderBottom: '1px solid var(--glass-border)',
            marginBottom: 'var(--space-sm)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: '500', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                {job.job_title}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {job.blocked_tasks} blocked, {job.at_risk_tasks} at risk
              </div>
            </div>
            <span style={{ 
              padding: '2px 6px', 
              background: `${riskColors[job.risk_level]}20`,
              color: riskColors[job.risk_level],
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.625rem',
              fontWeight: '600'
            }}>
              {job.risk_level}
            </span>
          </div>
          {job.risk_reasons?.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              {job.risk_reasons[0]}
            </div>
          )}
        </div>
      ))}
    </GlassPanel>
  );
}

export default GanttChart;
